import React from 'react';

export type PrimaryButtonProps = {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  className?: string;
  fullWidth?: boolean;
  rounded?: string; // ex: 'rounded', 'rounded-md', 'rounded-full'
  bgColor?: string;
  textColor?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  variant?: 'filled' | 'outline' | 'ghost' | 'dashed';
};

const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  children,
  onClick,
  type = 'button',
  disabled = false,
  className = '',
  fullWidth = false,
  rounded = 'rounded-full',
  bgColor,
  textColor,
  icon,
  iconPosition = 'left',
  variant = 'filled',
}) => {
  // Styles de base
  let base = 'font-semibold px-8 py-3 transition-colors duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400';
  const width = fullWidth ? 'w-full block' : '';

  // Styles selon la variante
  let variantStyle = '';
  let disabledStyle = '';

  switch (variant) {
    case 'outline':
      variantStyle = 'border border-blue-600 bg-transparent text-blue-600';
      disabledStyle = 'border-blue-200 text-blue-200 bg-transparent cursor-not-allowed opacity-60';
      break;
    case 'ghost':
      variantStyle = 'bg-transparent text-blue-600';
      disabledStyle = 'text-blue-200 bg-transparent cursor-not-allowed opacity-60';
      break;
    case 'dashed':
      variantStyle = 'border border-dashed border-blue-600 bg-transparent text-blue-600';
      disabledStyle = 'border-blue-200 text-blue-200 bg-transparent cursor-not-allowed opacity-60';
      break;
    case 'filled':
    default:
      variantStyle = 'bg-blue-600 text-white';
      disabledStyle = 'bg-blue-300 text-blue-100 cursor-not-allowed opacity-70';
      break;
  }

  // Ajoute les couleurs custom sans jamais retirer les classes par défaut
  if (bgColor && bgColor !== '') {
    variantStyle += ` ${bgColor}`;
  }
  if (textColor && textColor !== '') {
    variantStyle += ` ${textColor}`;
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={[
        base,
        rounded,
        width,
        variantStyle,
        disabled ? disabledStyle : '',
        className,
      ].join(' ').trim()}
    >
      {icon && iconPosition === 'left' && (
        <span className="inline-flex items-center mr-2 align-middle">{icon}</span>
      )}
      <span className="align-middle">{children}</span>
      {icon && iconPosition === 'right' && (
        <span className="inline-flex items-center ml-2 align-middle">{icon}</span>
      )}
    </button>
  );
};

export default PrimaryButton; 